// Notifications slice — the data seam for the header bell's all-plan ("global")
// feed shown on the landing page. Lives centrally in store/slices (one slice per
// concern). It always fetches the backend `/notifications` for the authenticated
// participant and maps it to the UI shape — the frontend mock is gone (fixture
// data is the backend's data/notifications.json via scripts/generate_data.py;
// the wire-shaped test fixture lives in src/test-utils/fixtures/
// notifications.mock.js). Components read camelCase only (Frontend Rules:
// mappers always, boundary kept).
//
// This is ADDITIVE: the per-plan notification feed (NotificationsMenu's static
// default, shown inside a selected plan) is unchanged and not backed by this slice.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from '../../services/api/client';

// ----- Mapper (the camelCase boundary) -----
// The wire is already camelCase (like /overview); `icon` stays a string kind and
// is resolved to an icon component in NotificationsMenu (no JSX in the slice).
export const mapNotificationsToUI = (api) =>
  (api.notifications || []).map((n) => ({
    id: n.id,
    icon: n.icon,
    tone: n.tone,
    title: n.title,
    detail: n.detail ?? null,
    hoursAgo: n.hoursAgo,
    unread: !!n.unread,
    planId: n.planId ?? null,
    planName: n.planName ?? null,
  }));

// ----- Thunk: the backend seam -----
// Live: GET /notifications?userId= → mapNotificationsToUI. Mock: the static mock.
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) return rejectWithValue('No participant selected.');
      const data = await apiGet(`/notifications?userId=${encodeURIComponent(userId)}`);
      return mapNotificationsToUI(data);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load notifications.');
    }
  },
  // Single-fetch guard (FRONTEND_RULES §7): skip when the SAME request is already
  // in flight OR already loaded; a different userId or a retry after failure runs.
  {
    condition: (userId, { getState }) => {
      const s = getState().notifications;
      if ((s.status === 'loading' || s.status === 'succeeded') && s.lastArg === userId) return false;
      return true;
    },
  },
);

// Starts empty; the bell renders its loading/empty state until the thunk resolves.
const initialState = {
  items: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  lastArg: undefined, // the userId the current data is for — drives the single-fetch guard
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.lastArg = action.meta.arg;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load notifications';
      });
  },
});

// ----- Selectors -----
export const selectNotifications = (state) => state.notifications.items;
export const selectNotificationsStatus = (state) => state.notifications.status;
export const selectNotificationsError = (state) => state.notifications.error;

export default notificationsSlice.reducer;
